import { getDisplayName, getSecondaryName, getInitials } from "../../data/clientDerive";

// ── Types ──────────────────────────────────────────────────────────────

/** Minimal customer shape needed for alias-aware rendering. */
export interface ClientNameCustomer {
  name: string;
  alias: string | null;
}

interface ClientNameProps {
  /** Customer object (needs at least name; alias is optional). */
  customer: ClientNameCustomer;

  /**
   * When true, renders the secondary name (real name) on a second line
   * beneath the display name. Only visible when an alias is set.
   * @default false
   */
  showSecondary?: boolean;

  /** CSS class applied to the primary (display) name span. */
  primaryClassName?: string;

  /** CSS class applied to the secondary (real name) span. */
  secondaryClassName?: string;
}

// ── Component ──────────────────────────────────────────────────────────

/**
 * Alias-aware client name renderer.
 *
 * Wraps the shared `getDisplayName` / `getSecondaryName` helpers so
 * every surface that shows a client name goes through the same logic.
 *
 * Usage:
 *   <ClientName customer={customer} />
 *   <ClientName customer={customer} showSecondary />
 *   <ClientName customer={customer} primaryClassName="text-[13.5px] font-medium" />
 */
export function ClientName({
  customer,
  showSecondary = false,
  primaryClassName = "",
  secondaryClassName = "text-ink-4",
}: ClientNameProps) {
  const display = getDisplayName(customer);
  const secondary = showSecondary ? getSecondaryName(customer) : null;

  return (
    <>
      <span className={primaryClassName}>{display}</span>
      {secondary && (
        <>
          <br />
          <span className={secondaryClassName}>{secondary}</span>
        </>
      )}
    </>
  );
}

// ── Static helpers re-exported for convenience ─────────────────────────

/**
 * Alias-aware initials for use with the Avatar component.
 * Re-export of `getInitials` from clientDerive for ergonomic imports.
 */
export function clientInitials(customer: ClientNameCustomer): string {
  return getInitials(customer);
}

/**
 * Alias-aware display name as a plain string.
 * Re-export of `getDisplayName` from clientDerive for ergonomic imports.
 */
export function clientDisplayName(customer: ClientNameCustomer): string {
  return getDisplayName(customer);
}
